import {
  fileLabel,
  fmtDayLong,
  fmtDayLongYear,
  fmtMonthDay,
  fmtTime,
  formatBytes,
  joinLinkService,
  joinWindow,
  utcToWall,
  zoneLabel,
  zoneName,
} from '@concord/domain';
import type { PatientCaseSummary, PatientHomeView } from '@concord/domain/contracts';
import { Button, Notice, Panel, PanelHead, PatientHead, PhaseButton } from '@concord/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { partOfDay } from '../console/ConsoleHome';
import s from './home.module.css';
import { ContactNavigator, PatientLayout } from './PatientLayout';

function useNow(everyMs = 30_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), everyMs);
    return () => clearInterval(t);
  }, [everyMs]);
  return now;
}

/**
 * The patient home (PAT-01 to PAT-05): the next consultation with a single join action,
 * case status in plain words, records, a new case subject to entitlement, and the care
 * navigator on every screen.
 */
export function PatientHome({ me }: { me: Me }) {
  const navigate = useNavigate();
  const now = useNow();
  const [contact, setContact] = useState(false);
  const home = useQuery({ queryKey: ['patient-home'], queryFn: () => api<PatientHomeView>('/patient/home') });
  const start = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/cases', {
        body: { device: window.matchMedia('(max-width: 640px)').matches ? 'phone' : 'computer' },
      }),
    onSuccess: (r) => navigate(`/cases/${r.id}/intake/type`),
  });

  const tz = me.timezone;
  const h = home.data;
  const next = h?.open.find((c) => c.consultation && c.consultation.state !== 'completed');
  const openCase = next ?? h?.open[0];
  const nav = me.navigator;

  const lead = (() => {
    if (!h) return fmtDayLongYear(now, tz);
    const parts = [`${fmtDayLongYear(now, tz)}.`];
    if (next?.consultation) {
      const same = utcToWall(new Date(next.consultation.startsAt), tz).date === utcToWall(now, tz).date;
      parts.push(
        same
          ? 'Your consultation is today.'
          : `Your consultation is ${fmtDayLong(new Date(next.consultation.startsAt), tz)}.`,
      );
    }
    const waiting = h.draft
      ? 'Your new case is not sent yet.'
      : openCase?.status.waitingOnYou
        ? `${openCase.status.waitingOnYou}.`
        : 'Nothing is waiting on you.';
    parts.push(waiting);
    return parts.join(' ');
  })();

  return (
    <PatientLayout me={me}>
      <PatientHead title={`Good ${partOfDay(now)}, ${me.firstName}`} lead={lead} />
      {home.isError && <Notice tone="bad">{errorMessage(home.error)}</Notice>}
      {h && (
        <div className={s.grid}>
          <div className={s.col}>
            {next?.consultation && (
              <NextConsultation c={next} tz={tz} now={now} windowMinutes={h.joinWindowMinutes} />
            )}

            {h.draft && (
              <section className={s.next} aria-label="Your case in progress">
                <div className={s.draft}>
                  <div className={s.b}>
                    <div className={s.h}>
                      {h.draft.caseType ? `${h.draft.caseType}, not sent yet` : 'Your new case, not sent yet'}
                    </div>
                    <div className={s.d}>
                      Started {fmtMonthDay(new Date(h.draft.startedAt), tz)}. Your answers are saved; pick up
                      where you left off, on any device.
                    </div>
                  </div>
                  <Button
                    onClick={() =>
                      navigate(
                        `/cases/${h.draft!.id}/intake/${['type', 'panel', 'about', 'history', 'records', 'times', 'review'][Math.min(6, h.draft!.intakeStep - 1)]}`,
                      )
                    }
                  >
                    Continue my case
                  </Button>
                </div>
              </section>
            )}

            {openCase && (
              <CaseCard
                c={openCase}
                count={h.open.length}
                navigatorFirst={nav?.firstName ?? 'your care navigator'}
              />
            )}

            {!openCase && !h.draft && (
              <Panel aria-label="Your case">
                <PanelHead title="Your case" sub="None open" />
                <div className={s.empty}>
                  When you send a case, where it stands will show here in plain words.
                </div>
              </Panel>
            )}

            <Panel aria-label="Your records">
              <PanelHead
                title="Your records"
                sub={`${h.records.length} ${h.records.length === 1 ? 'file' : 'files'}`}
              />
              {h.records.length === 0 && (
                <div className={s.empty}>Records you add to a case appear here.</div>
              )}
              {h.records.map((r) => (
                <div key={r.id} className={s.file}>
                  <span className={s.ic}>{fileLabel(r.filename, r.contentType)}</span>
                  <div className={s.b}>
                    <div className={s.n1}>{r.description ?? r.filename}</div>
                    <div className={s.n2}>
                      {fileLabel(r.filename, r.contentType)} · {formatBytes(r.sizeBytes)} ·{' '}
                      {r.sharedWith.length
                        ? `shared with ${r.sharedWith.map((n) => n.replace(/^Dr\. \S+ /, 'Dr. ')).join(', ')}`
                        : 'not shared yet'}
                    </div>
                  </div>
                  <a className={s.dl} href={`/api/attachments/${r.id}/download`}>
                    Download
                  </a>
                </div>
              ))}
            </Panel>
          </div>

          <div className={s.col}>
            <Panel aria-label="Your care navigator">
              <div className={s['nav-card']}>
                <div className={s.who}>
                  <span className={s.av}>
                    {nav?.name
                      ? nav.name
                          .split(' ')
                          .map((p) => p[0])
                          .join('')
                          .slice(0, 2)
                      : 'CN'}
                  </span>
                  <div>
                    <div className={s.n1}>{nav?.name ?? 'Your care navigator'}</div>
                    <div className={s.n2}>Your care navigator at Cancer Expert Now</div>
                  </div>
                </div>
                <div className={s.row}>
                  <Button onClick={() => setContact(true)}>Send a message</Button>
                  {nav?.phone && (
                    <a className="btn sec" href={`tel:${nav.phone.replace(/[^\d+]/g, '')}`}>
                      Call {nav.phone}
                    </a>
                  )}
                </div>
                <div className={s.hrs}>
                  {nav?.firstName ?? 'Your navigator'}{' '}
                  {nav?.hours?.replace(/^Replies/, 'replies') ?? 'replies within one business day.'} For
                  anything urgent about your health, contact your own doctor.
                </div>
              </div>
            </Panel>

            <Panel aria-label="Start a new case">
              <div className={s.newcase}>
                <div className={s.h}>Need another opinion?</div>
                <div className={s.d}>
                  You can start a new case any time. It takes about 20 minutes, and you can stop and come back
                  from any device.
                </div>
                {h.entitlement.valid ? (
                  <div className={s.ent}>
                    <span className={s.g} />
                    Covered by your {h.entitlement.customerName} {h.entitlement.planName?.toLowerCase()}
                  </div>
                ) : (
                  <div className={`${s.ent} ${s.bad}`}>
                    <span className={s.g} />
                    {h.entitlement.message} Contact your care navigator.
                  </div>
                )}
                {start.isError && <Notice tone="bad">{errorMessage(start.error)}</Notice>}
                <Button
                  variant="sec"
                  style={{ alignSelf: 'flex-start' }}
                  disabled={!h.entitlement.valid || Boolean(h.draft)}
                  busy={start.isPending}
                  onClick={() => start.mutate()}
                  title={h.draft ? 'Finish the case you started first.' : undefined}
                >
                  Start a new case
                </Button>
              </div>
            </Panel>

            {h.earlier.length > 0 && (
              <Panel aria-label="Earlier cases">
                <PanelHead title="Earlier cases" sub={h.earlier.length} />
                {h.earlier.map((c) => (
                  <div key={c.id} className={s.past}>
                    <div className={s.b}>
                      <div className={s.n1}>{c.caseType ?? 'Case'}</div>
                      <div className={s.n2}>
                        {c.reference} ·{' '}
                        {c.state === 'canceled'
                          ? 'Canceled'
                          : `Closed ${c.closedAt ? fmtMonthDay(new Date(c.closedAt), tz) : ''}`}
                      </div>
                    </div>
                  </div>
                ))}
              </Panel>
            )}
          </div>
        </div>
      )}
      <ContactNavigator open={contact} onOpenChange={setContact} me={me} />
    </PatientLayout>
  );
}

function NextConsultation({
  c,
  tz,
  now,
  windowMinutes,
}: {
  c: PatientCaseSummary;
  tz: string;
  now: Date;
  windowMinutes: number;
}) {
  const k = c.consultation!;
  const start = new Date(k.startsAt);
  const w = joinWindow(start, k.durationMinutes, windowMinutes, now);
  const today = utcToWall(start, tz).date === utcToWall(now, tz).date;
  const when = `${today ? 'Today' : fmtDayLong(start, tz)} at ${fmtTime(start, tz)} ${zoneName(tz, start).replace(' Time', '')}`;
  const physicians = k.physicians.map(
    (p) => `${p.name}${p.specialty ? `, ${p.specialty.toLowerCase()} specialist` : ''}`,
  );
  const opens = new Date(start.getTime() - windowMinutes * 60_000);
  return (
    <section className={s.next} aria-label="Your next consultation">
      <div className={s.top}>
        <div className={s.when}>
          <span className="key">Your consultation</span>
          <span className={s.d}>{k.state === 'provisional' ? `${when}, to be confirmed` : when}</span>
          <span className={s.t}>
            {physicians.length > 0 && (
              <>
                With <b>{physicians[0]}</b>
                {physicians.length > 1 && ` and ${physicians.length - 1} more`} ·{' '}
              </>
            )}
            {k.durationMinutes} minutes · video
          </span>
        </div>
        <div className={s.joinbox}>
          {w.status === 'open' && k.joinUrl ? (
            <a className="btn lg" href={k.joinUrl} target="_blank" rel="noopener noreferrer">
              Join the consultation
            </a>
          ) : (
            <button type="button" className="btn lg" disabled>
              Join the consultation
            </button>
          )}
          <span className={s.why}>
            {w.status === 'not_yet'
              ? `Opens at ${fmtTime(opens, tz)}${w.minutesUntilOpen <= 120 ? `, in ${w.minutesUntilOpen} minutes` : `, ${fmtDayLong(opens, tz)}`}. This page will let you in when it is time.`
              : w.status === 'open'
                ? `The consultation room is open. It opens in ${joinLinkService(k.joinUrl ?? '') ?? 'the video service'}, in a new tab.`
                : 'This consultation has ended.'}
          </span>
        </div>
      </div>
      <div className={s.foot}>
        <span>Times are shown in {zoneLabel(tz)}.</span>
        <span>
          <a href={`/api/patient/consultations/${c.id}/calendar.ics`}>Add to my calendar</a>
        </span>
        <PhaseButton phase="P2" variant="ghost">
          Test my camera and microphone
        </PhaseButton>
      </div>
    </section>
  );
}

function CaseCard({
  c,
  count,
  navigatorFirst,
}: {
  c: PatientCaseSummary;
  count: number;
  navigatorFirst: string;
}) {
  return (
    <Panel aria-label="Your case">
      <PanelHead title="Your case" sub={`${count} open`} />
      <div className={s.case}>
        <div className={s.st}>
          {c.caseType ?? 'Your case'}
          {c.reference && <span className="ref">{c.reference}</span>}
        </div>
        {c.status.waitingOnYou ? (
          <div className={s.waiting} style={{ color: 'var(--alert)' }}>
            <span className={s.g} style={{ background: 'var(--alert)' }} />
            {c.status.waitingOnYou}
          </div>
        ) : (
          <div className={s.waiting}>
            <span className={s.g} />
            Nothing is waiting on you
          </div>
        )}
        <div className={s.plain}>
          <b>{c.status.headline}.</b> {c.status.explanation.replace('Your care navigator', navigatorFirst)}
        </div>
        <div className={s.track}>
          {c.status.track.map((t) => (
            <div
              key={t.label}
              className={`${s.s} ${t.status === 'done' ? s.done : t.status === 'now' ? s.now : ''}`}
            >
              <i />
              <span>{t.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}
